import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { paymentsShape } from 'constants/propTypes';
import { COVID_VOUCHER_REFUND, paymentMethodTranslation, providers } from 'constants/payment';

import moment from 'moment';
import Button from 'components/ui/Button';
import { CurrencyInput } from 'components/ui/Inputs';
import CardSelect from 'components/ui/CardSelect';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { ADD_ADDITIONAL_DETAILS, ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { useMutation } from '@apollo/client';

import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import { getAllPaymentsPerMethod, getAmountToRefundPerMethod } from '../../Payments.utils';

import { ErrorMessage, FormWrapper, ProviderLabel, Spacer, AmountLabel } from './CardRefund.styles';
import PaymentAgeWarning from '../PaymentAgeWarning';

const CardRefund = ({
  bookingReference,
  currency,
  loading,
  processCardPaymentRefund,
  totalPriceToBePaid,
  paymentLocation,
  payments,
  method,
  isCancelVoucher,
  isStation,
  mainBookingRef,
  hasHotel
}) => {
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);
  const allCardPayments = getAllPaymentsPerMethod(payments, method);
  const cardTotal = getAmountToRefundPerMethod(payments, method);

  const maxAllowedRefundAmount = Number(Math.min(totalPriceToBePaid, cardTotal).toFixed(2));

  const { register, handleSubmit, clearErrors, formState, watch } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;
  const selectedReference = watch('reference');

  const [refundAmount, setAmount] = useState(maxAllowedRefundAmount);
  const [maxCardAmount, setMaxCardAmount] = useState(maxAllowedRefundAmount);

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);

  if (!allCardPayments || allCardPayments.length === 0) {
    return (
      <p>
        <FormattedMessage id="refunds.card.noCards" />
      </p>
    );
  }

  if (cardTotal === 0) {
    return (
      <p data-cy="noRefundLeft">
        <FormattedMessage id="refunds.card.noRefundLeftOnMethod" />
      </p>
    );
  }

  const hasCardError = Boolean(errors.reference);
  const hasAmountError = Boolean(errors.amount);

  const handleOnSubmit = ({ reference, amount }) => {
    processCardPaymentRefund({
      variables: {
        bookingReference,
        refundAmount: Number(amount),
        pspReference: reference,
        currency,
        paymentLocation,
        hasHotel
      }
    });

    if (isStation && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
      addAdditionalDetails({
        variables: {
          bookingReference,
          additionalDetails: [
            {
              key: `tablet-refund-${moment().format('HH:mm:ss.DDMMYY')}`,
              value: `${paymentLocation}-${Number(refundAmount).toFixed(2)}`
            }
          ]
        }
      });
    }

    if (isCancelVoucher) {
      addNote({
        variables: {
          note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
          bookingReference: mainBookingRef
        }
      });

      addNote({
        variables: {
          note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
          bookingReference
        }
      });
    }
  };

  return (
    <form data-testid="card-refund-form" data-cy="refund-form" onSubmit={handleSubmit(handleOnSubmit)}>
      <FormWrapper>
        <header>
          <h2 data-cy="title">
            <FormattedMessage id="refunds.card.title" />
          </h2>
          <p data-cy="paragraph">
            <FormattedMessage id="refunds.card.paragraph" />
          </p>
        </header>
        <h3>
          <FormattedMessage id="refunds.card.selectCard" />
        </h3>
        <Spacer>
          <CurrencyInput
            currency={currency}
            dataCy="amount-input"
            dataTestId="amount-input"
            defaultValue={maxAllowedRefundAmount}
            isControlled={false}
            {...register('amount', {
              required: true,
              min: 0.01,
              max: maxCardAmount,
              onChange: (e) => setAmount(e.target.value)
            })}
            step={0.01}
          />
        </Spacer>
        {allCardPayments.map(
          ({
            reference,
            externalReference,
            provider,
            currency: paymentCurrency,
            amount,
            amountInOriginalCurrency,
            availableRefundAmount,
            method
          }) => {
            const availableAmount = availableRefundAmount || 0;

            return (
              <CardSelect
                key={reference}
                id={reference}
                testId={reference}
                value={reference}
                disabled={currency !== paymentCurrency || availableAmount <= 0}
                onClick={() => setMaxCardAmount(availableAmount)}
                {...register('reference', { required: true, onChange: () => clearErrors('reference') })}
              >
                <div>
                  <span data-testid={`card-${reference}`} data-test={`card-${reference}`}>
                    {externalReference ? (
                      `.... .... .... .... ${externalReference}`
                    ) : (
                      <FormattedMessage id={paymentMethodTranslation[method]} />
                    )}
                  </span>
                  <ProviderLabel data-test={`card-${reference}-provider`}>
                    {provider === providers.ADYEN && 'adyen'}
                    {provider === providers.THALYS && 'thalys'}
                  </ProviderLabel>
                </div>
                <div>
                  <AmountLabel>
                    <FormattedMessage id="refunds.original" />
                  </AmountLabel>
                  <FormattedCurrency
                    dataCy={`original-amount-${reference}`}
                    dataTestId={`original-amount-${reference}`}
                    currency={paymentCurrency}
                    amount={amountInOriginalCurrency || amount}
                  />
                </div>
                <div>
                  <AmountLabel>
                    <FormattedMessage id="refunds.available" />
                  </AmountLabel>
                  <FormattedCurrency
                    dataCy={`available-amount-${reference}`}
                    dataTestId={`available-amount-${reference}`}
                    currency={paymentCurrency}
                    amount={availableAmount}
                  />
                </div>
              </CardSelect>
            );
          }
        )}
        {hasAmountError && (
          <ErrorMessage data-cy="amount-error-msg">
            <FormattedMessage
              id="refunds.card.error.amount"
              values={{
                amount: <FormattedCurrency currency={currency} amount={maxCardAmount} />
              }}
            />
          </ErrorMessage>
        )}
        {hasCardError && (
          <ErrorMessage data-cy="card-error-msg">
            <FormattedMessage id="refunds.card.error.selectCard" />
          </ErrorMessage>
        )}
        {maxCardAmount < refundAmount && (
          <ErrorMessage data-cy="card-max-amount-error-msg">
            <FormattedMessage id="refunds.card.maxAmountError" />
            <FormattedCurrency currency={currency} amount={maxCardAmount} />
          </ErrorMessage>
        )}
        <PaymentAgeWarning
          payment={allCardPayments.find((payment) => payment.reference === selectedReference)}
          amount={refundAmount}
          maxAmount={maxCardAmount}
          hasError={hasAmountError || hasCardError}
        />
        <Button
          data-testid="submit"
          data-cy="submit"
          type="submit"
          loading={loading || addAdditionalDetailsLoading}
          disabled={loading || maxCardAmount < refundAmount}
          variant="primary"
        >
          <FormattedMessage id="refunds.card.submit" />
        </Button>
      </FormWrapper>
    </form>
  );
};

CardRefund.propTypes = {
  currency: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  processCardPaymentRefund: PropTypes.func.isRequired,
  paymentLocation: PropTypes.string.isRequired,
  isStation: PropTypes.bool,
  bookingReference: PropTypes.string.isRequired,
  totalPriceToBePaid: PropTypes.number.isRequired,
  payments: paymentsShape,
  method: PropTypes.string,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  hasHotel: PropTypes.bool
};
export default CardRefund;
